import type {NuxtError} from "#app";
import {createError, useFetch, useRuntimeConfig} from '#app'
import type {Ref} from "vue";
import {watch} from "vue";
import {type FetchOptions} from "ofetch";

export interface UseApiFetchOptions extends FetchOptions {

    // Whether to not await the response before rendering pages, i.e., data will be loaded after the component is rendered
    // Defaults to false
    lazy?: boolean;

    // Whether an error shall be displayed via a redirect to the error page even though the request was lazy
    // By default, the error page is shown when the request is not lazy and only an error instance is returned when the request is lazy
    // Defaults to false
    fatalError?: boolean;

    // Whether to fetch the data on the server side
    // Defaults to true
    server?: boolean;

    // Whether the request shall be immediately send on the SSR or CSR (depending on the other options), defaults to true.
    // If set to false, the request must be manually triggered via the refresh function or a watcher to be sent.
    immediate?: boolean;
}

function throwErrorIfFatal(error: any, options: UseApiFetchOptions) {
    if ((!options.lazy || options.fatalError)) {
        throw createError({
            statusCode: error.statusCode,
            statusMessage: error.statusMessage,
            message: error.message,
            fatal: true
        } as NuxtError);
    }
}

/**
 * Composable to fetch data from the GraphQL API.
 */
export const useApiFetch = async (command: string, selectProps: string[], filter: string | undefined = undefined, options: UseApiFetchOptions = {}): Promise<object> => {
    const config = useRuntimeConfig();

    const query = `{
${command}${filter ? `(${filter})` : ''} {
    data {
      ${selectProps.join(' ')}
    }
  }
}`;

    const headers: any = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.public.apiToken}`
    };

    // Set the defaults
    options.server = options.server ?? true;
    options.lazy = options.lazy ?? false;
    options.immediate = options.immediate ?? true;

    const {data, pending, error, refresh}: {
        data: Ref<object>,
        pending: Ref<boolean>,
        error: Ref<any | null>,
        refresh: () => void
    } = await useFetch('/cms/read/de-DE', {
        baseURL: config.public.apiUrl as string,
        method: 'POST',

        // The custom options passed as param
        ...options,

        // Add the options that can not be overridden by the options param below
        body: JSON.stringify({
            query: query
        }),
        headers: headers,

        server: options.server,
        immediate: options.immediate,
    });

    // Watcher is required to show the error page on client and server side
    if (error.value) {
        throwErrorIfFatal(error.value, options);
    }
    watch(error, (newValue: any) => {
        if (newValue) {
            throwErrorIfFatal(newValue, options)
        }
    });

    // Errors from the API are returned as 200 OK with an errors array in the response
    const errors = data.value?.errors;
    if (errors) {
        throwErrorIfFatal({
            statusCode: 500,
            statusMessage: 'Internal Server Error',
            message: errors.map((error: any) => error.message).join('\n'),
        }, options)
    }

    const responseData = data.value?.data?.[command]?.data;

    // Commands starting with "get" are expected to return a single object, so a 404 is thrown if no data is returned
    if (!responseData && command.startsWith('get')) {
        throwErrorIfFatal({
            statusCode: 404,
            statusMessage: 'Not Found',
            message: 'The requested resource could not be found.',
        }, options)
    }

    return {
        error,
        refresh,
        pending,
        data: responseData
    };
}

